import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {RegisterService} from "./register.service";
import {HeaderService} from "../header/header.service";
import {Router} from "@angular/router";
import {DictResponse} from "../activate/dict-response";
import {NotifierService} from "../notifier/notifier.service";
import {NotificationType} from "../notifier/notification-type.enum";
import {IsQueryResponse} from "./is-query-response";
import {forkJoin} from "rxjs/observable/forkJoin";
import {CommonService} from "../../common/common.service";
import {Dictionary} from "../../model/dictionary";
import {User} from "./user";
import {Constants} from "../../common/constants";
import {UserCookiesService} from "../user-cookies/user-cookies.service";
import {UserPrivacyService} from "../user-privacy/user-privacy.service";
import {UserRulesService} from "../user-rules/user-rules.service";
import {PasswordValidator} from "../../common/password.validator";

function validateFirstNameLastName(c : FormControl) {
  let value = c.value.trim();
  if (value.length === 0)
    return {
      validate: {valid : false}
    }
  else
    return null;
}

@Component({
  selector: 'pbc-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {

  newUserData: User;

  registerFormStepOne: FormGroup;
  registerFormStepTwo: FormGroup;
  registerFormStepFour: FormGroup;

  dictAreas: Array<DictResponse>;
  dictPosition : Array<Dictionary>;
  positionHint : string = "";

  safeDomainLogo : any;

  step: number = 1;

  constructor(private registerService: RegisterService,
              private headerService: HeaderService,
              private router: Router,
              private userCookiesService : UserCookiesService,
              private userPrivacyService : UserPrivacyService,
              private userRulesService : UserRulesService,
              private commonService : CommonService,
              private notifierService: NotifierService) {
  }

  static isInteger(value:any){
    return ((parseFloat(value) == parseInt(value)) && !isNaN(value));
  };

  /**
   * Waliduje cały formularz. Formularz jest poprawny wttw kiedy hasło i pole w którym je potwierdzamy mają takie same
   * wartości.
   * @param form
   */
  private validatePasswords(form: FormGroup) : {[key: string] : any} {

    let password = form.value['password'];
    let confirmPassword = form.value['password-repeat'];

    if (password==confirmPassword) {
      form.controls['password-repeat'].setErrors(null);
      return null;
    } else {
      form.controls['password-repeat'].setErrors({'notMatch': true});
      return {passwordNotConfirmed: true};
    }
  }

  private validateInteger = (control:FormControl) => {
    if (control.value !== null && control.value.length>0) {
      return RegisterComponent.isInteger(control.value) ? null : {
        notNumeric: true
      }
    } else {
      return null;
    }
  };

  onResendEmail() {
    this.registerService.sendActivationEmail(this.newUserData.email).subscribe(
      response => {
        if (response.status !=1)
          this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Błąd: Nie można wysłać maila aktyawcyjnego");
        else
          this.notifierService.notify(NotificationType.SUCCESS_NOTIFICATION, "Email aktywacyjny został wysłany");
      },
      error => {
        console.log('Nie mozna wysłać maila aktywacyjnego ', error);
        this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Błąd w trakcie wysyłania maila aktywacyjnego");
      }
    )
  }

  createLogoFromBlob(image: Blob) {
    let reader = new FileReader();
    reader.addEventListener("load", () => {
      this.safeDomainLogo = reader.result;
    }, false);

    if (image) {
      reader.readAsDataURL(image);
    }
  }

  onNextStep() {
    switch (this.step) {
      case 1: {

        let email = this.registerFormStepOne.controls['email'].value;
        forkJoin(
          this.commonService.userExists(email),
          this.registerService.isDomainSafe(email)
        ).subscribe(([userExistResponse, isDomainSafeResponse])=>{
          if ((<IsQueryResponse>userExistResponse).status !==1) {
            this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Nie można sprawdzić czy taki użytkownik istnieje");
          }
          if ((<IsQueryResponse>isDomainSafeResponse).status !==1) {
            this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Nie można sprawdzić domeny użytkownika");
          }

          if ((<IsQueryResponse>userExistResponse).message) {
            this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Konto z podanym adresem email istnieje już w serwisie");
          } else {
            if ((<IsQueryResponse>isDomainSafeResponse).message) {
              this.newUserData = new User(this.registerFormStepOne.controls['firstName'].value,
                this.registerFormStepOne.controls['lastName'].value,
                this.registerFormStepOne.controls['email'].value);

              forkJoin(
                this.registerService.getArea(),
                this.commonService.getSafeDomainLogo(this.newUserData.email)
              ).subscribe(([getAreaResponse, safeDomainLogo])=>{
                this.dictAreas = getAreaResponse;
                this.createLogoFromBlob(safeDomainLogo);
              });

              this.registerService.getArea().subscribe(
                (response: Array<DictResponse>) => {
                  this.dictAreas = response;
                });
              //Domyślna wartośc w polu kontakt
              this.registerFormStepTwo.controls['preferredFormOfContact'].setValue('email');

              this.step++;
            } else {
              this.registerFormStepFour.controls['firstName'].setValue(this.registerFormStepOne.controls['firstName'].value);
              this.registerFormStepFour.controls['lastName'].setValue(this.registerFormStepOne.controls['lastName'].value);
              this.registerFormStepFour.controls['email'].setValue(this.registerFormStepOne.controls['email'].value);
              this.step = 4;
            }
          }

        },
          (error)=> {
            console.log(error);
            this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Nie można sprawdzić domeny użytkownika");
          }
        );
        break;

      }
      case 2: {

        this.newUserData.password = this.registerFormStepTwo.controls['password'].value;
        this.newUserData.workPhone = this.registerFormStepTwo.controls['workPhone'].value;
        this.newUserData.preferredFormOfContact = this.registerFormStepTwo.controls['preferredFormOfContact'].value;
        this.newUserData.position = this.registerFormStepTwo.controls['position'].value;
        this.newUserData.area = this.registerFormStepTwo.controls['area'].value;
        this.newUserData.workExperience = this.registerFormStepTwo.controls['workExperience'].value;
        this.newUserData.agreement = true;

        this.registerService.registerUser(this.newUserData).subscribe(
          (response)=>{
            console.log(response);
            this.step++;
          },
          (error)=>{
            console.log(error);
          }
        );

        break;
      }
    }

    let scrollToTop = window.setInterval(() => {
      let pos = window.pageYOffset;
      if (pos > 0) {
        window.scrollTo(0, pos - 20); // how far to scroll on each step
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 16);

  }

  ngOnInit() {
    this.headerService.isHomepage(false);
    this.headerService.hideLoginRegisterButtons();

    this.registerFormStepOne = new FormGroup({
      'firstName': new FormControl('',[Validators.required, validateFirstNameLastName]),
      'lastName': new FormControl('', [Validators.required, validateFirstNameLastName]),
      'email': new FormControl('', [Validators.required, Validators.email])
    });

    this.registerFormStepTwo = new FormGroup({
      'password': new FormControl('', [Validators.required,
        PasswordValidator.strong,
        Validators.minLength(6)]),
      'password-repeat': new FormControl('', [Validators.required,
        PasswordValidator.strong,
        Validators.minLength(6)
      ]),
      'workPhone': new FormControl(''),
      'preferredFormOfContact': new FormControl('', Validators.required),
      'position': new FormControl('', Validators.required),
      'area': new FormControl(''),
      'workExperience': new FormControl('', [this.validateInteger, Validators.min(0), Validators.max(50)]),
      'agreement-correct-data': new FormControl('', Validators.requiredTrue),
      'agreement-rules': new FormControl('', Validators.requiredTrue)
    });

    this.registerFormStepTwo.setValidators(this.validatePasswords);

    this.registerFormStepFour = new FormGroup({
      'firstName': new FormControl('', [Validators.required, validateFirstNameLastName]),
      'lastName': new FormControl('', [Validators.required, validateFirstNameLastName]),
      'email': new FormControl('', [Validators.required, Validators.email]),
      'position': new FormControl(''),
      'phone': new FormControl(''),
      'send-copy': new FormControl(false),
      'agreement-rules': new FormControl(false, Validators.requiredTrue),
      'message': new FormControl(Constants.MAIL_REGISTER_STEP_FOUR, Validators.required)
    });

    this.commonService.getPositionDictionary().subscribe(
      (response) => {
        this.dictPosition = response;
    },
      error=>{
        console.log(error);
        this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, "Błąd w trakcie pobierania słownika");
      })
  }

  onSendEnquire() {
    let firstName = this.registerFormStepFour.controls['firstName'].value;
    let lastName = this.registerFormStepFour.controls['lastName'].value;
    let email = this.registerFormStepFour.controls['email'].value;
    let position = this.registerFormStepFour.controls['position'].value;
    let phone = this.registerFormStepFour.controls['phone'].value;
    let sendCopy = this.registerFormStepFour.controls['send-copy'].value;
    let userMessage = this.registerFormStepFour.controls['message'].value;

    this.registerService.sendEnquiry(firstName, lastName, email, userMessage, sendCopy, position, phone).subscribe(
        (response: {status: number, message: string}) => {
          if (response.status==1) {
            this.notifierService.notify(NotificationType.SUCCESS_NOTIFICATION, "Zapytanie o dostęp do serwisu zostało wysłane do administratora. Odpowiedź powinna być wysłana w ciągu kilku najbliższych dni.");

            this.registerFormStepFour.controls['firstName'].setValue("");
            this.registerFormStepFour.controls['lastName'].setValue("");
            this.registerFormStepFour.controls['email'].setValue("");
            this.registerFormStepFour.controls['position'].setValue("");
            this.registerFormStepFour.controls['phone'].setValue("");
            this.registerFormStepFour.controls['send-copy'].setValue(false);
            this.registerFormStepFour.controls['message'].setValue(Constants.MAIL_REGISTER_STEP_FOUR);

          } else {
            this.notifierService.notify(NotificationType.ERROR_NOTIFICATION, response.message);
          }
        },
        (error) => {
          console.log(error);
        }
    );
  }

  showPrivacyClick() {
    this.userPrivacyService.showPrivacyRulesPopup(true);
  }

  showRulesClick() {
    this.userRulesService.showUserRulesPopup(true)
  }

  onPasswordDirty() {
    this.registerFormStepTwo.controls['position'].markAsDirty();
    this.registerFormStepTwo.controls['preferredFormOfContact'].markAsDirty();
  }

  positionChange() {
    let positionSelected = this.registerFormStepTwo.controls["position"].value;
    for (let i = 0; i < this.dictPosition.length; i++)
      if (this.dictPosition[i].id === positionSelected) {
        this.positionHint = this.dictPosition[i].value_1;
        break;
      }
  }
}
